/**
 * Phase 6 (2026-05-25) — First-run guided spotlight tour.
 *
 * Replaces the disabled C.12 FirstVisitOverlay topic-tile picker and
 * the (now-deleted) /welcome route + "Show me around again" Start
 * Menu item with a single 4-step Clippy-narrated spotlight tour
 * anchored on the Win95 desktop icons.
 *
 * Fires automatically the first time a portal-less user lands on the
 * desktop:
 *   - Signed-in Clerk user with empty portalAccess.listMyPortals() → []
 *   - Signed-out showcase visitor (useShowcaseVisible[0] === true)
 *
 * The Start Menu "Take the tour" item re-launches the tour at any
 * time, regardless of the localStorage completion flag.
 *
 * Pacing budget: 45–60 seconds total. Each step is ≤2 short
 * sentences. No auto-advance — the user controls pacing via the
 * "Next" button.
 *
 * Telemetry: every transition fires a nipeAuditLog event (paired
 * live + demo). `detail` carries the stepIndex as a string ("0".."3")
 * so we can build a per-step drop-off funnel without widening the
 * schema. See convex/nipeAuditLog.ts.
 *
 * Z-index: 2056 — above NipeIntroBalloon (2055), below the modal
 * dialog zone (10000+) so import/conflict wizards still float over
 * the tour if they fire mid-tour (rare).
 *
 * Pointer events: the dimmed backdrop is `pointerEvents: 'none'` so
 * clicks pass through to the underlying highlighted window — the user
 * CAN explore (right-click a doc, open the matter tree, etc.) while
 * the tour is open. Only the yellow bubble re-enables pointer events
 * (so Skip / Next stay clickable). Step 1's "right-click any document"
 * instruction would be a lie otherwise.
 *
 * Final-step CTA branches by mode:
 *   - signed-in:  "Start with Set up SharePoint" → focuses + opens
 *                  the /onboard/new window.
 *   - signed-out: leaves the final-step window open as the user's
 *                  next action. No auto-navigate to the sign-up
 *                  surface — see the inline comment on handleNext
 *                  for the rationale.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useWindowContext } from '../desktop/WindowContext';
import { useShowcaseSessionId } from '../../lib/showcase/showcaseSession';
import { testIds } from '../../lib/testIds';

export type FirstRunTourMode = 'signed-in' | 'signed-out-showcase';

export interface FirstRunTourStep {
  /** Path that resolves to the desktop icon's data-testid. */
  iconPath: string;
  /** Bold heading rendered at the top of the bubble. */
  title: string;
  /** Body copy. Keep to two short sentences. */
  short: string;
  /**
   * Optional override when the spotlighted element isn't a desktop
   * icon (e.g. the NIPE sprite). Wins over `iconPath` when set.
   */
  targetSelector?: string;
  /**
   * When the user clicks Next on this step, also open this window.
   * Defaults to `iconPath`. Set `false` to skip (e.g. for NIPE which
   * has no window — the sprite is always visible).
   */
  openOnNext?: string | false;
}

// Step order: let the user LOOK AROUND first (the sample workspace
// already has seeded matters/docs they can poke at) before pushing
// them to set up their own SharePoint. The "Set up SharePoint" step
// is intentionally LAST so the tour reads as "here's what Repancy
// can do for you → now go provision it for your firm" instead of
// "you must provision before you can do anything." The seeded sample
// workspace makes every preceding step explorable without provisioning.
export const TOUR_STEPS: ReadonlyArray<FirstRunTourStep> = [
  {
    iconPath: '/documents',
    title: '1. Document Library',
    short:
      "Browse the sample matters + files. Click a matter on the left, then right-click any document row → 'Send to Prompt Lab' to bring it into the AI workbench.",
  },
  {
    iconPath: '/prompt-lab',
    title: '2. Prompt Lab',
    short:
      "Run prompts against any document — summarize, redline, cite-check. Save useful prompts as reusable Skills.",
  },
  {
    iconPath: '/skills',
    title: '3. Skill Library',
    short:
      "Skills are pre-authored prompt fragments your firm shares. Pick one to apply its style to your next run.",
  },
  {
    iconPath: '/specialization-lab',
    title: '4. Specialization Lab',
    short:
      "Specialize a model against your firm's corpus. Compare base vs specialized side-by-side on real prompts.",
  },
  {
    // NIPE has no window path; the Clippy agent renders its own
    // bottom-right sprite directly on document.body via
    // clippy/engine/agent.ts (which adds `class="clippy"`). The
    // ClippyHost component itself returns null + has no data-testid
    // to hook into, so the selector targets the agent's class.
    iconPath: '__nipe-sprite__',
    targetSelector: '.clippy',
    openOnNext: false,
    title: "5. Meet NIPE",
    short:
      "Your AI sidekick. Click NIPE any time for context-aware help — what to do next, why something failed, what a Skill expects.",
  },
  {
    iconPath: '/obsidian-vault',
    title: "6. Obsidian Explorer",
    short:
      "Your firm's knowledge base as markdown — Clients, Subjects, and Prompts already preloaded with sample notes. Use the Import vault button in the toolbar to bring in your own folder, zip, or SharePoint pull.",
  },
  {
    iconPath: '/onboard/new',
    title: "7. Provision your own portal",
    short:
      "Ready? Create your firm's portal in ~90 seconds. The sample data you've been exploring becomes your starter template.",
  },
];

export interface FirstRunTourProps {
  mode: FirstRunTourMode;
  onDismiss: () => void;
  onComplete: () => void;
  /**
   * Portal slug for telemetry. Signed-out visitors have no portal —
   * pass the sentinel `'__no-portal__'` so paired-audit-table writes
   * still succeed.
   */
  clientSlug?: string;
}

interface CutoutRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface StepCutouts {
  /**
   * The PRIMARY rect — the spotlight that the bubble anchors to. Window
   * frame when available, NIPE sprite for the NIPE step, icon as
   * fallback (e.g. during the first frames after a step advances and
   * the window hasn't mounted yet).
   */
  primary: CutoutRect;
  /**
   * The icon rect, ALWAYS included when present (so the user sees the
   * desktop icon lit alongside the window — visual association between
   * "click that icon" and "this is the window it opens"). Null for the
   * NIPE step (no icon — the sprite IS the primary).
   */
  icon: CutoutRect | null;
  /**
   * The stepIndex these rects were measured for. Render guards against
   * stale cutouts whose `stepIndex` doesn't match the current step —
   * without this, on Next the OLD step's cutout persists for one frame
   * (the layout-effect's `setCutouts(prev => prev)` "preserve" branch
   * keeps the stale value alive until the poll catches the new window
   * mount, producing the left-then-right flash the operator sees).
   */
  stepIndex: number;
}

const TELEMETRY_SLUG_SENTINEL = '__no-portal__';

function rectFromElement(el: Element | null): CutoutRect | null {
  if (el === null) return null;
  const rect = (el as HTMLElement).getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  const pad = 6;
  return {
    x: rect.left - pad,
    y: rect.top - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  };
}

function readStepCutouts(
  step: FirstRunTourStep,
  stepIndex: number,
  /**
   * When true, refuse to return until the WINDOW rect is available
   * (returning null otherwise). This prevents the bubble from briefly
   * rendering next to the desktop icon while the freshly-opened
   * window is still mounting.
   *
   * The icon-only fallback is still useful for the LAST recompute
   * attempt (after ~600ms the window really should be mounted; if
   * it's not, anchoring to the icon is better than no spotlight).
   */
  requireWindowOrSprite: boolean,
): StepCutouts | null {
  if (typeof document === 'undefined') return null;
  // NIPE step: sprite is the only target.
  if (step.targetSelector !== undefined) {
    const sprite = rectFromElement(document.querySelector(step.targetSelector));
    if (sprite === null) return null;
    return { primary: sprite, icon: null, stepIndex };
  }
  const iconRect = rectFromElement(
    document.querySelector(
      `[data-testid="desktop-icon-${step.iconPath.replace(/\//g, '')}"]`,
    ),
  );
  const windowRect = rectFromElement(
    document.querySelector(
      `[data-testid="window-frame-${step.iconPath.replace(/\//g, '')}"]`,
    ),
  );
  if (windowRect !== null) {
    return { primary: windowRect, icon: iconRect, stepIndex };
  }
  if (requireWindowOrSprite) {
    return null;
  }
  if (iconRect === null) return null;
  return { primary: iconRect, icon: iconRect, stepIndex };
}

export function FirstRunTour({
  mode,
  onDismiss,
  onComplete,
  clientSlug,
}: FirstRunTourProps): ReactElement | null {
  const winCtx = useWindowContext();
  const showcaseSessionId = useShowcaseSessionId();
  const logEvent = useMutation(api.nipeAuditLog.logEvent);
  const [stepIndex, setStepIndex] = useState(0);
  const [cutouts, setCutouts] = useState<StepCutouts | null>(null);
  const fireOnceRef = useRef(false);

  const isShowcase = mode === 'signed-out-showcase';
  const slug = clientSlug ?? TELEMETRY_SLUG_SENTINEL;
  const currentStep = TOUR_STEPS[stepIndex];
  const isLastStep = stepIndex === TOUR_STEPS.length - 1;

  const fire = (event:
    | 'tour-displayed'
    | 'tour-step-viewed'
    | 'tour-step-skipped'
    | 'tour-completed'
    | 'tour-dismissed',
    detail?: string,
  ) => {
    void logEvent({
      clientSlug: slug,
      event,
      ...(detail !== undefined ? { detail } : {}),
      ...(isShowcase ? { demoSessionId: showcaseSessionId } : {}),
    }).catch(() => undefined);
  };

  // tour-displayed fires once on mount.
  useEffect(() => {
    if (fireOnceRef.current) return;
    fireOnceRef.current = true;
    fire('tour-displayed');
    fire('tour-step-viewed', '0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recompute the cutout whenever the step changes OR the window
  // resizes. useLayoutEffect so the cutout lands in the same frame as
  // the bubble — no flicker between empty backdrop and lit overlay.
  //
  // Also poll briefly after step transitions: when handleNext opens
  // a new window, the window mounts on the next frame and its
  // bounding rect isn't available on the layout-effect tick that
  // immediately follows setStepIndex. Re-read every 50ms for the
  // first 500ms so the spotlight catches up to the freshly-mounted
  // window without an awkward icon-then-window jump.
  useLayoutEffect(() => {
    let cancelled = false;
    // Note: deliberately DO NOT setCutouts(null) here. Nulling out
    // would unrender the cutout `<rect>` inside the SVG mask for one
    // frame between the old step's cutout and the new step's cutout,
    // which means a frame of no-spotlight while the dim still paints.
    // That LOOKS fine (dim stays) but the operator-perceived "blink"
    // was actually that frame transitioning from dim+spotlight →
    // dim+no-spotlight → dim+spotlight. Keep the stale cutout in
    // state; the freshness gate (`haveFreshCutouts`, by stepIndex)
    // hides the visible bubble + uses the stale cutout in the mask
    // as a transient hold so the SPOTLIGHT also stays lit on the
    // old window for a frame instead of vanishing. Operator-reported
    // step-5-doesn't-blink confirmed root cause: NIPE's sprite is
    // always-mounted, so cutouts populate synchronously in the same
    // tick — no stale-or-null transition window.

    // Poll schedule. The first 5 attempts REQUIRE the window rect
    // to be available; the final attempt (600ms) accepts the icon
    // fallback. Anything <= 50ms after step change is too early for
    // a freshly-opened window to have layout.
    function recompute(allowIconFallback: boolean) {
      if (cancelled) return;
      if (currentStep === undefined) {
        setCutouts(null);
        return;
      }
      setCutouts((prev) => {
        const next = readStepCutouts(
          currentStep,
          stepIndex,
          !allowIconFallback,
        );
        if (next === null) {
          // Window not mounted yet. KEEP prev even if it's stale
          // (from the previous step) — the freshness gate at render
          // time hides the bubble while stale, but the spotlight
          // cutout remains painted so the dim overlay never has a
          // no-spotlight frame. That eliminates the operator-reported
          // blink on Next.
          return prev;
        }
        if (
          prev !== null &&
          prev.stepIndex === next.stepIndex &&
          prev.primary.x === next.primary.x &&
          prev.primary.y === next.primary.y &&
          prev.primary.width === next.primary.width &&
          prev.primary.height === next.primary.height &&
          (prev.icon?.x ?? null) === (next.icon?.x ?? null) &&
          (prev.icon?.y ?? null) === (next.icon?.y ?? null)
        ) {
          return prev;
        }
        return next;
      });
    }
    // Initial attempt: REQUIRE the window. If it's not up yet, null
    // result — bubble stays unrendered.
    recompute(false);
    const handles: ReturnType<typeof setTimeout>[] = [];
    for (const delay of [50, 150, 300, 500]) {
      handles.push(setTimeout(() => recompute(false), delay));
    }
    // Final attempt: allow icon fallback so we always show SOMETHING
    // by 600ms even if the window genuinely failed to mount.
    handles.push(setTimeout(() => recompute(true), 600));
    function onResize() {
      recompute(true);
    }
    window.addEventListener('resize', onResize);
    return () => {
      cancelled = true;
      for (const h of handles) clearTimeout(h);
      window.removeEventListener('resize', onResize);
    };
  }, [currentStep, stepIndex]);

  // Escape dismisses.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        fire('tour-dismissed', String(stepIndex));
        onDismiss();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, onDismiss]);

  /** Returns the window path a step targets, or null if the step
   *  has no associated window (e.g. NIPE sprite step). */
  function windowPathForStep(step: FirstRunTourStep | undefined): string | null {
    if (!step) return null;
    if (step.openOnNext === false) return null;
    const target = step.openOnNext ?? step.iconPath;
    if (!target.startsWith('/')) return null;
    return target;
  }

  function openStepWindow(step: FirstRunTourStep | undefined) {
    const target = windowPathForStep(step);
    if (target === null) return;
    const win = winCtx.getWindow(target);
    if (!win || win.state === 'closed' || win.state === 'minimized') {
      winCtx.openWindow(target);
    } else {
      winCtx.focusWindow(target);
    }
  }

  function minimizeStepWindow(step: FirstRunTourStep | undefined) {
    const target = windowPathForStep(step);
    if (target === null) return;
    const win = winCtx.getWindow(target);
    // Only minimize if it's actually visible — don't touch closed
    // windows. Avoids the case where the user manually closed the
    // window during the tour and we'd re-open it via minimize state
    // (minimize keeps the window mounted, just hidden from view).
    if (win && win.state === 'open') {
      winCtx.minimizeWindow(target);
    }
  }

  function handleNext() {
    if (isLastStep) {
      fire('tour-completed', String(stepIndex));
      // onComplete writes the tour-completed flag + resolves the
      // attention orchestrator turn + flips wantsToShow → false,
      // which unmounts THIS component. No more overlay after this
      // call returns — see useFirstRunTour.markComplete.
      onComplete();
      // Leave the final-step window open as the user's next action.
      // No surprise navigate-away: "Finish tour" means finish the
      // tour, not silently teleport to the sign-up surface.
      openStepWindow(currentStep);
      return;
    }
    // Mid-tour Next behavior:
    //   1. Minimize the CURRENT step's window so the screen is clean
    //      for the next step's spotlight (previously windows stacked
    //      up and the spotlight would just light a corner of the last
    //      window opened).
    //   2. Advance to the next step.
    //   3. The step-change effect below opens the new step's window
    //      so the spotlight has something to land on.
    // The minimize happens FIRST, synchronously — earlier code
    // deferred it 600ms which created the "window opens then vanishes"
    // jank the user saw.
    minimizeStepWindow(currentStep);
    const next = stepIndex + 1;
    setStepIndex(next);
    fire('tour-step-viewed', String(next));
  }

  // Open the current step's window when the step changes. The
  // spotlight's readTargetRect prefers the open window's bounding
  // rect (with the poll-after-step fallback above to catch the
  // window's mount frame), so the cutout lands on the window
  // content the bubble is describing instead of a desktop icon
  // that's typically off-screen by the time the user has clicked
  // through to step 3+.
  useEffect(() => {
    if (currentStep === undefined) return;
    // Cutouts cleared synchronously by the layout-effect above (runs
    // before paint, so the next paint after Next never inherits the
    // prior step's coordinates). Just open the new step's window.
    openStepWindow(currentStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  function handleSkip() {
    fire('tour-step-skipped', String(stepIndex));
    fire('tour-dismissed', String(stepIndex));
    onDismiss();
  }

  if (typeof document === 'undefined') return null;
  if (currentStep === undefined) return null;

  // Operator-reported "blue flash" root cause (confirmed by console
  // logs): the overlay was returning null when cutouts was unknown
  // OR stale, unmounting the entire fixed-position div. React then
  // re-mounted on the next render — the browser composited the
  // missing frame as a flash of the bare desktop. Fix: never
  // unmount. The overlay stays in the React tree continuously; we
  // toggle visibility:hidden when cutouts are unknown or belong to
  // a different step. visibility:hidden does NOT trigger a
  // mount/unmount and does NOT paint anything — same effect as
  // returning null, but without the compositor blink.
  //
  // Two conditions cause hide:
  //   1. cutouts === null (between transitions, before poll catches
  //      the new window mount)
  //   2. cutouts.stepIndex !== stepIndex (stale — measured for the
  //      previous step; would render bubble + spotlight at the old
  //      position for one paint frame before the layout-effect
  //      catches up)
  const haveFreshCutouts =
    cutouts !== null && cutouts.stepIndex === stepIndex;
  const bubbleWidth = 320;
  const bubbleGap = 16;
  let bubbleLeft = 0;
  let bubbleTop = 0;
  if (haveFreshCutouts) {
    const p = cutouts.primary;
    const flipLeft = p.x + p.width + bubbleGap + bubbleWidth > window.innerWidth;
    bubbleLeft = flipLeft
      ? Math.max(8, p.x - bubbleGap - bubbleWidth)
      : p.x + p.width + bubbleGap;
    bubbleTop = Math.max(8, p.y);
  }

  const overlay = (
    <div
      data-testid={testIds.nipe.tourRoot}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2056,
        // pointerEvents: 'none' on the root so the dimmed backdrop
        // NEVER blocks clicks on the underlying window. The bubble
        // re-enables pointer events on itself below so Skip / Next
        // remain clickable.
        pointerEvents: 'none',
        // Root is ALWAYS visible while the tour mounts. The dim
        // overlay must stay painted between step transitions so the
        // user never sees a frame of the bare desktop (the
        // operator-reported "blink"). Per-element visibility gates
        // live on the cutout + bubble below — those can flicker
        // through stale→fresh while the dim itself stays solid.
      }}
    >
      {/* SVG backdrop with cutout. The mask hides a rectangle equal
          to the target icon's bounding rect, leaving the icon visible
          through the 0.55-alpha overlay. pointerEvents: 'none' so the
          dimmed area is purely visual — no click interception. */}
      <svg
        data-testid={testIds.nipe.tourBackdrop}
        width="100%"
        height="100%"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'block',
          pointerEvents: 'none',
        }}
      >
        <defs>
          <mask id="first-run-tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {/* Cutout renders whenever we have SOME cutout data,
                even if it's stale (from the previous step). The
                stale version paints the spotlight on the OLD window
                for a transient frame between step change and the
                poll catching the new window mount — looks like a
                soft hand-off rather than a blink-to-black. The
                bubble is hidden during this transient via the
                haveFreshCutouts gate below, so the user doesn't see
                stale TEXT, only the stale spotlight position. */}
            {cutouts !== null && (
              <>
                <rect
                  data-testid={testIds.nipe.tourCutout}
                  x={cutouts.primary.x}
                  y={cutouts.primary.y}
                  width={cutouts.primary.width}
                  height={cutouts.primary.height}
                  rx={6}
                  ry={6}
                  fill="black"
                />
                {cutouts.icon !== null && (
                  <rect
                    x={cutouts.icon.x}
                    y={cutouts.icon.y}
                    width={cutouts.icon.width}
                    height={cutouts.icon.height}
                    rx={6}
                    ry={6}
                    fill="black"
                  />
                )}
              </>
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.55)"
          mask="url(#first-run-tour-mask)"
        />
      </svg>

      {/* Tour bubble — Clippy-style yellow card pointing at the cutout.
          pointerEvents: 'auto' re-enables interaction on the bubble
          alone so Skip / Next stay clickable while the dimmed backdrop
          around it is click-through. */}
      <div
        data-testid={testIds.nipe.tourBubble}
        data-step-index={stepIndex}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: bubbleTop,
          left: bubbleLeft,
          width: bubbleWidth,
          padding: 14,
          background: '#fffbcc',
          border: '1px solid #b08800',
          boxShadow: '3px 3px 0 rgba(0,0,0,0.35)',
          fontFamily: 'var(--xp-font-body)',
          fontSize: 12,
          color: '#000',
          lineHeight: 1.4,
          // Bubble hides while cutouts are stale (between step
          // change and the layout-effect catching the new window
          // mount) so the user doesn't see new step's title at the
          // OLD step's position for a frame. The dim + spotlight
          // stay painted (see SVG mask above) — only the bubble
          // flickers, and visibility:hidden keeps it mounted so no
          // remount. pointerEvents disabled while hidden too.
          visibility: haveFreshCutouts ? 'visible' : 'hidden',
          pointerEvents: haveFreshCutouts ? 'auto' : 'none',
        }}
      >
        <div
          data-testid={testIds.nipe.tourBubbleStep(stepIndex)}
          style={{ fontWeight: 'bold', marginBottom: 6, fontSize: 13 }}
        >
          {currentStep.title}
        </div>
        <div style={{ marginBottom: 10 }}>{currentStep.short}</div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            fontSize: 11,
          }}
        >
          <span style={{ color: '#374151' }}>
            Step {stepIndex + 1} of {TOUR_STEPS.length}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            {/* Skip tour button hidden on the final step — there's
                nothing left to skip; the only meaningful action is
                finishing the tour. */}
            {!isLastStep && (
              <button
                type="button"
                data-testid={testIds.nipe.tourSkip}
                onClick={handleSkip}
                style={{
                  fontFamily: 'var(--xp-font-body)',
                  fontSize: 11,
                  padding: '3px 10px',
                  background: '#ece9d8',
                  borderStyle: 'solid',
                  borderWidth: 1,
                  color: '#000',
                  cursor: 'pointer',
                }}
              >
                Skip tour
              </button>
            )}
            {isLastStep ? (
              <button
                type="button"
                data-testid={testIds.nipe.tourCta}
                onClick={handleNext}
                style={{
                  fontFamily: 'var(--xp-font-body)',
                  fontSize: 11,
                  fontWeight: 'bold',
                  padding: '3px 12px',
                  background: '#d0d0f0',
                  borderStyle: 'solid',
                  borderWidth: 1,
                  color: '#000080',
                  cursor: 'pointer',
                }}
              >
                Finish tour
              </button>
            ) : (
              <button
                type="button"
                data-testid={testIds.nipe.tourNext}
                onClick={handleNext}
                style={{
                  fontFamily: 'var(--xp-font-body)',
                  fontSize: 11,
                  fontWeight: 'bold',
                  padding: '3px 16px',
                  background: '#d0d0f0',
                  borderStyle: 'solid',
                  borderWidth: 1,
                  color: '#000080',
                  cursor: 'pointer',
                }}
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
